import { useMemo, useState } from "react";
import {
  flattenDimensionItems,
  getActiveDimensionKeys,
} from "../Categories/components/Category/helpers";
import { getDimensionItemName } from "./dimensionDisplay";

/**
 * Shared filter + client-side pagination logic for the Scope 2 v2 data-entry
 * tables (Purchased Electricity, On-site Generation), mirroring the Scope 3
 * CategoryGrid pattern so all scope task tables behave consistently.
 *
 * We fetch the full dataset upstream (size 9999), so every filter can see every
 * row rather than only the page the server returned.
 *
 * @param organizationStructure org config (drives the active dimension columns)
 * @param rows raw API records (dimensionItems nested, other fields at top level)
 * @param filterColumns non-dimension filterable columns:
 *   [{ key, label, getValue(flatRow) => string }]
 *
 * Returns decorated rows (flattened + `__label_*` + `__record` pointer to the
 * original raw record), the filter-bar inputs, the current page slice, and the
 * pagination controls.
 */
export function useV2TableFilters(organizationStructure, rows, filterColumns = []) {
  const [filters, setFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const activeDimensionKeys = useMemo(
    () => getActiveDimensionKeys(organizationStructure),
    [organizationStructure]
  );

  // Flatten each record for display and annotate with `__label_*` values so the
  // filter bar can compare like-for-like. Keep `__record` pointing at the raw
  // record — the edit/delete modals need the nested dimensionItems that
  // flattening strips.
  const decoratedRows = useMemo(() => {
    return (rows || []).map((record) => {
      const flat = flattenDimensionItems({ ...record });
      const decorated = { ...flat, __record: record };
      activeDimensionKeys.forEach((dimKey) => {
        decorated[`__label_${dimKey}`] = getDimensionItemName(
          organizationStructure,
          dimKey,
          flat[`${dimKey}Item`]
        );
      });
      filterColumns.forEach((col) => {
        const v = col.getValue(flat);
        decorated[`__label_${col.key}`] = v == null || v === "" ? "" : String(v);
      });
      return decorated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, organizationStructure, activeDimensionKeys]);

  // Grouped distinct labels per active dimension → the rolled-up Dimension pill.
  const dimensionGroups = useMemo(() => {
    const groups = [];
    activeDimensionKeys.forEach((dimKey) => {
      const labels = new Set();
      decoratedRows.forEach((row) => {
        const label = row[`__label_${dimKey}`];
        if (label) labels.add(label);
      });
      const items = [...labels].sort().map((label) => ({ label, value: label }));
      if (items.length > 0) {
        groups.push({
          dimKey,
          name: organizationStructure.dimensions[dimKey].name,
          items,
        });
      }
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decoratedRows, organizationStructure, activeDimensionKeys]);

  // Non-dimension column pills — driven by whatever distinct labels appear.
  // Skip columns whose values are all numeric with many distinct values
  // (filtering by exact numeric values doesn't aid discovery).
  const extraFilterColumns = useMemo(() => {
    return filterColumns
      .map((col) => {
        const labels = new Set();
        let allNumeric = true;
        decoratedRows.forEach((row) => {
          const v = row[`__label_${col.key}`];
          if (!v) return;
          labels.add(v);
          if (Number.isNaN(Number(v))) allNumeric = false;
        });
        if (labels.size === 0) return null;
        if (allNumeric && labels.size > 4) return null;
        const options = [...labels].sort().map((v) => ({ label: v, value: v }));
        return { key: col.key, label: col.label, options };
      })
      .filter(Boolean);
  }, [decoratedRows, filterColumns]);

  const filteredItems = useMemo(() => {
    const dimensionSelections = filters._dimension || [];
    const activeExtras = Object.entries(filters).filter(
      ([key, vals]) => key !== "_dimension" && Array.isArray(vals) && vals.length > 0
    );
    if (dimensionSelections.length === 0 && activeExtras.length === 0) return decoratedRows;

    return decoratedRows.filter((row) => {
      if (dimensionSelections.length > 0) {
        const byDim = dimensionSelections.reduce((acc, sel) => {
          (acc[sel.dimKey] ||= []).push(sel.label);
          return acc;
        }, {});
        for (const [dimKey, labels] of Object.entries(byDim)) {
          const rowLabel = row[`__label_${dimKey}`];
          if (!labels.includes(rowLabel)) return false;
        }
      }
      for (const [key, vals] of activeExtras) {
        const rowLabel = row[`__label_${key}`];
        if (!vals.some((v) => v.value === rowLabel)) return false;
      }
      return true;
    });
  }, [decoratedRows, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const pageSlice = useMemo(
    () => filteredItems.slice(safePageIndex * pageSize, (safePageIndex + 1) * pageSize),
    [filteredItems, safePageIndex, pageSize]
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPageIndex(0);
  };
  const clearAllFilters = () => {
    setFilters({});
    setPageIndex(0);
  };
  const handlePageSelection = (newPage) => setPageIndex(newPage - 1);
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPageIndex(0);
  };

  const isFiltered = filteredItems.length !== decoratedRows.length;

  return {
    dimensionGroups,
    extraFilterColumns,
    filters,
    handleFilterChange,
    clearAllFilters,
    filteredItems,
    pageSlice,
    totalPages,
    safePageIndex,
    pageSize,
    handlePageSelection,
    handlePageSizeChange,
    totalCount: decoratedRows.length,
    isFiltered,
  };
}
